import { Form, Formik } from 'formik';
import { useReducer } from 'react';
import { Plug2 } from 'lucide-react';

import { useCreateAgentEnvironmentMutation } from '@/react/portainer/environments/queries/useCreateEnvironmentMutation';
import { notifySuccess } from '@/portainer/services/notifications';
import {
  ContainerEngine,
  Environment,
} from '@/react/portainer/environments/types';
import { CreateAgentEnvironmentValues } from '@/react/portainer/environments/environment.service/create';
import { CustomTemplate } from '@/react/portainer/templates/custom-templates/types';
import { getVariablesFieldDefaultValues } from '@/react/portainer/custom-templates/components/CustomTemplatesVariablesField';
import { NameField } from '@/react/portainer/environments/common/NameField/NameField';
import { EnvironmentUrlField } from '@/react/portainer/environments/common/EnvironmentUrlField/EnvironmentUrlField';

import { LoadingButton } from '@@/buttons/LoadingButton';

import { MoreSettingsSection } from '../MoreSettingsSection';
import { CustomTemplateSelector } from '../CustomTemplateSelector';

import { useValidation } from './AgentForm.validation';

interface Props {
  onCreate(environment: Environment): void;
  customTemplates?: CustomTemplate[];
  envDefaultPort?: string;
  containerEngine?: ContainerEngine;
}

const initialValues: CreateAgentEnvironmentValues = {
  environmentUrl: '',
  name: '',
  meta: {
    groupId: 1,
    tagIds: [],
    customTemplateId: 0,
    variables: getVariablesFieldDefaultValues([]),
  },
};

export function AgentForm({
  onCreate,
  customTemplates,
  envDefaultPort,
  containerEngine = ContainerEngine.Docker,
}: Props) {
  const [formKey, clearForm] = useReducer((state) => state + 1, 0);

  const mutation = useCreateAgentEnvironmentMutation();
  const validation = useValidation();

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validation}
      validateOnMount
      key={formKey}
    >
      {({ isValid, dirty }) => (
        <Form>
          <NameField />
          <EnvironmentUrlField placeholderPort={envDefaultPort} />

          <MoreSettingsSection>
            {customTemplates && (
              <CustomTemplateSelector customTemplates={customTemplates} />
            )}
          </MoreSettingsSection>

          <div className="form-group">
            <div className="col-sm-12">
              <LoadingButton
                className="wizard-connect-button vertical-center"
                data-cy="agent-connect-environment-button"
                loadingText="Connecting environment..."
                isLoading={mutation.isLoading}
                disabled={!dirty || !isValid}
                icon={Plug2}
              >
                Connect
              </LoadingButton>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );

  function handleSubmit(values: CreateAgentEnvironmentValues) {
    mutation.mutate(
      { ...values, containerEngine },
      {
        onSuccess(environment) {
          notifySuccess('Environment created', environment.Name);
          clearForm();
          onCreate(environment);
        },
      }
    );
  }
}
